import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Wallet,
  Users,
  UserCheck,
  FileText,
  CreditCard,
  DollarSign,
  Calendar,
  CheckCircle,
  XCircle,
  List
} from 'lucide-react';
import { apiEndpoints, handleApiError } from '../services/api';

const FinanceOverview = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [personnel, setPersonnel] = useState([]);
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [counts, setCounts] = useState({ income: 0, expense: 0, personnel: 0 });

  // View state
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'income' | 'expense' | 'personnel'
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [jumpPage, setJumpPage] = useState('');

  // Modal state
  const [modalMode, setModalMode] = useState(null); // 'income' | 'expense' | 'personnel'
  const [editing, setEditing] = useState(null);

  const today = new Date().toISOString().split('T')[0];

  const [form, setForm] = useState({
    personnelId: '',
    paymentType: 'Salary',
    description: '',
    category: '',
    amount: '',
    paymentDate: today,
    paymentMethod: 'Bank Transfer',
    status: '',
    notes: ''
  });

  const incomeCategories = [
    { value: 'Maintenance', label: t('incomes.categories.maintenance') },
    { value: 'Equipment', label: t('incomes.categories.equipment') },
    { value: 'Supplies', label: t('incomes.categories.supplies') },
    { value: 'Utilities', label: t('incomes.categories.utilities') },
    { value: 'Insurance', label: t('incomes.categories.insurance') },
    { value: 'Marketing', label: t('incomes.categories.marketing') },
    { value: 'Other', label: t('incomes.categories.other') }
  ];

  const expenseTypes = [
    'Equipment Purchase',
    'Office Supplies',
    'Maintenance',
    'Utilities',
    'Insurance',
    'Transportation',
    'Marketing',
    'Training',
    'Legal Fees',
    'Rent',
    'Other'
  ];

  const expenseCategories = [
    'Equipment',
    'Operations',
    'Administrative',
    'Marketing',
    'Maintenance',
    'Utilities',
    'Other'
  ];

  const personnelPaymentTypes = [
    { value: 'Salary', label: t('common.salary') },
    { value: 'Advance', label: t('common.advance') },
    { value: 'Bonus', label: t('common.bonus') },
    { value: 'Overtime', label: t('common.overtime') },
    { value: 'Commission', label: t('common.commission') },
    { value: 'Other', label: t('common.other') }
  ];

  const paymentMethods = [
    { value: 'Bank Transfer', label: t('common.bankTransfer') },
    { value: 'Cash', label: t('common.cash') },
    { value: 'Check', label: t('common.check') },
    { value: 'Other', label: t('common.other') }
  ];

  const expenseStatusOptions = [
    { value: 0, label: t('common.completed') },
    { value: 1, label: t('common.pending') },
    { value: 2, label: t('common.cancelled') },
    { value: 3, label: t('common.refunded') }
  ];

  const personnelStatusOptions = [
    { value: 1, label: t('common.processed') },
    { value: 0, label: t('common.pending') },
    { value: 2, label: t('common.cancelled') }
  ];

  const fetchPersonnel = useCallback(async () => {
    try {
      const [coachResp, adminResp] = await Promise.all([
        apiEndpoints.users.getByRole('Coach'),
        apiEndpoints.users.getByRole('Admin')
      ]);
      setPersonnel([
        ...(coachResp.data?.users || []).map(u => ({ ...u, role: 'Coach' })),
        ...(adminResp.data?.users || []).map(u => ({ ...u, role: 'Admin' }))
      ]);
    } catch (error) {
      handleApiError(error, 'Failed to load personnel');
    }
  }, []);

  useEffect(() => {
    fetchPersonnel();
  }, [fetchPersonnel]);

  const getCategoryLabel = useCallback((kind, category) => {
    if (kind === 'income') {
      const labels = {
        Maintenance: t('incomes.categories.maintenance'),
        Equipment: t('incomes.categories.equipment'),
        Supplies: t('incomes.categories.supplies'),
        Utilities: t('incomes.categories.utilities'),
        Insurance: t('incomes.categories.insurance'),
        Marketing: t('incomes.categories.marketing'),
        Other: t('incomes.categories.other')
      };
      return labels[category] || category;
    }
    return category || '—';
  }, [t]);

  const mapIncome = useCallback((inc) => ({
    kind: 'income',
    id: inc.id,
    title: inc.description || '—',
    sub: '',
    category: inc.category,
    amount: inc.amount || 0,
    date: inc.paymentDate,
    status: 'paid',
    notes: inc.notes
  }), []);

  const mapExpense = useCallback((exp) => ({
    kind: 'expense',
    id: exp.id,
    paymentType: exp.paymentType,
    title: exp.description || exp.paymentType || '—',
    sub: '',
    category: exp.category,
    amount: exp.amount || 0,
    date: exp.paymentDate,
    status: exp.status,
    notes: exp.notes
  }), []);

  const mapPersonnel = useCallback((pp) => ({
    kind: 'personnel',
    id: pp.id,
    personnelId: pp.personnelId,
    paymentType: pp.paymentType,
    title: pp.personnelName || pp.description || pp.paymentType || '—',
    sub: '',
    category: pp.paymentType,
    amount: pp.amount || 0,
    date: pp.paymentDate,
    status: pp.status,
    notes: pp.notes
  }), []);

  const fetchPage = useCallback(async () => {
    try {
      setLoading(true);
      let pageRows = [];
      let total = 0;

      if (activeTab === 'income') {
        const resp = await apiEndpoints.miscellaneousPayments.getAll({ page: currentPage, pageSize: itemsPerPage });
        pageRows = (resp.data?.items || []).map(mapIncome);
        total = resp.data?.totalCount ?? pageRows.length;
        setCounts(c => ({ ...c, income: total }));
      } else if (activeTab === 'expense') {
        const resp = await apiEndpoints.institutionalPayments.getPaginated({ page: currentPage, limit: itemsPerPage });
        pageRows = (resp.data?.payments || []).map(mapExpense);
        total = resp.data?.totalItems ?? pageRows.length;
        setCounts(c => ({ ...c, expense: total }));
      } else if (activeTab === 'personnel') {
        const resp = await apiEndpoints.personnelPayments.getPaginated({ page: currentPage, limit: itemsPerPage });
        pageRows = (resp.data?.payments || []).map(mapPersonnel);
        total = resp.data?.totalItems ?? pageRows.length;
        setCounts(c => ({ ...c, personnel: total }));
      } else {
        const limit = Math.max(currentPage * itemsPerPage, itemsPerPage);
        const [incomesResp, expensesResp, personnelResp] = await Promise.all([
          apiEndpoints.miscellaneousPayments.getAll({ page: 1, pageSize: limit }),
          apiEndpoints.institutionalPayments.getPaginated({ page: 1, limit }),
          apiEndpoints.personnelPayments.getPaginated({ page: 1, limit })
        ]);
        const incTotal = incomesResp.data?.totalCount ?? 0;
        const expTotal = expensesResp.data?.totalItems ?? 0;
        const perTotal = personnelResp.data?.totalItems ?? 0;
        setCounts({ income: incTotal, expense: expTotal, personnel: perTotal });
        const allRows = [
          ...(incomesResp.data?.items || []).map(mapIncome),
          ...(expensesResp.data?.payments || []).map(mapExpense),
          ...(personnelResp.data?.payments || []).map(mapPersonnel)
        ].sort((a, b) => new Date(b.date) - new Date(a.date));
        pageRows = allRows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
        total = incTotal + expTotal + perTotal;
      }

      setRows(pageRows);
      setTotalRecords(total);
    } catch (error) {
      handleApiError(error, 'Failed to load finance data');
    } finally {
      setLoading(false);
    }
  }, [activeTab, currentPage, itemsPerPage, mapIncome, mapExpense, mapPersonnel]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const getStatusInfo = (kind, status) => {
    if (kind === 'income') {
      return { label: t('finance.paid'), cls: 'bg-green-100 text-green-700' };
    }
    if (kind === 'expense') {
      switch (status) {
        case 0: return { label: t('common.completed'), cls: 'bg-green-100 text-green-700' };
        case 1: return { label: t('common.pending'), cls: 'bg-yellow-100 text-yellow-700' };
        case 2: return { label: t('common.cancelled'), cls: 'bg-red-100 text-red-700' };
        default: return { label: t('common.refunded'), cls: 'bg-blue-100 text-blue-700' };
      }
    }
    switch (status) {
      case 1: return { label: t('finance.paid'), cls: 'bg-green-100 text-green-700' };
      case 0: return { label: t('common.pending'), cls: 'bg-yellow-100 text-yellow-700' };
      default: return { label: t('common.cancelled'), cls: 'bg-red-100 text-red-700' };
    }
  };

  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return rows;
    const q = searchTerm.toLowerCase();
    return rows.filter(r =>
      (r.title || '').toLowerCase().includes(q) ||
      (r.sub || '').toLowerCase().includes(q) ||
      (r.notes || '').toLowerCase().includes(q)
    );
  }, [rows, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(totalRecords / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const openModal = (mode, payment = null) => {
    setEditing(payment);
    setModalMode(mode);
    if (mode === 'income') {
      setForm({
        ...form,
        personnelId: '',
        paymentType: '',
        description: payment?.description || '',
        category: payment?.category || '',
        amount: payment?.amount?.toString() || '',
        paymentDate: payment?.date?.split('T')[0] || today,
        paymentMethod: 'Bank Transfer',
        status: '',
        notes: payment?.notes || ''
      });
    } else if (mode === 'expense') {
      setForm({
        ...form,
        personnelId: '',
        paymentType: payment?.paymentType || 'Equipment Purchase',
        description: payment?.description || '',
        category: payment?.category || 'Equipment',
        amount: payment?.amount?.toString() || '',
        paymentDate: payment?.date?.split('T')[0] || today,
        paymentMethod: payment?.paymentMethod || 'Bank Transfer',
        status: payment?.status !== undefined ? payment.status : 0,
        notes: payment?.notes || ''
      });
    } else {
      setForm({
        ...form,
        personnelId: payment?.personnelId?.toString() || '',
        paymentType: payment?.paymentType || 'Salary',
        description: payment?.description || '',
        category: '',
        amount: payment?.amount?.toString() || '',
        paymentDate: payment?.date?.split('T')[0] || today,
        paymentMethod: payment?.paymentMethod || 'Bank Transfer',
        status: payment?.status !== undefined ? payment.status : 1,
        notes: payment?.notes || ''
      });
    }
  };

  const closeModal = () => {
    setModalMode(null);
    setEditing(null);
  };

  const switchTransactionType = (mode) => {
    if (mode === modalMode) return;
    setEditing(null);
    setModalMode(mode);
    setForm({
      ...form,
      personnelId: '',
      paymentType: mode === 'expense' ? 'Equipment Purchase' : 'Salary',
      description: '',
      category: mode === 'expense' ? 'Equipment' : '',
      amount: '',
      paymentDate: today,
      paymentMethod: 'Bank Transfer',
      status: mode === 'expense' ? 0 : 1,
      notes: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalMode === 'income') {
        const data = {
          description: form.description,
          amount: parseFloat(form.amount),
          category: form.category,
          paymentDate: new Date(form.paymentDate).toISOString(),
          notes: form.notes || ''
        };
        if (editing) {
          await apiEndpoints.miscellaneousPayments.update(editing.id, data);
          toast.success(t('incomes.paymentUpdated'));
        } else {
          await apiEndpoints.miscellaneousPayments.create(data);
          toast.success(t('incomes.paymentAdded'));
        }
      } else if (modalMode === 'expense') {
        const data = {
          paymentType: form.paymentType,
          description: form.description,
          amount: parseFloat(form.amount),
          paymentDate: new Date(form.paymentDate).toISOString(),
          category: form.category,
          notes: form.notes || '',
          status: Number(form.status),
          paymentMethod: form.paymentMethod,
          referenceNumber: ''
        };
        if (editing) {
          await apiEndpoints.institutionalPayments.update(editing.id, data);
          toast.success('Expense updated');
        } else {
          await apiEndpoints.institutionalPayments.create(data);
          toast.success('Expense created');
        }
      } else {
        const data = {
          personnelId: parseInt(form.personnelId),
          paymentType: form.paymentType,
          description: form.description,
          amount: parseFloat(form.amount),
          paymentDate: form.paymentDate,
          notes: form.notes || '',
          status: Number(form.status),
          paymentMethod: form.paymentMethod,
          referenceNumber: ''
        };
        if (editing) {
          await apiEndpoints.personnelPayments.update(editing.id, data);
          toast.success(t('institutional.personnelPayments.editPayment'));
        } else {
          await apiEndpoints.personnelPayments.create(data);
          toast.success(t('institutional.personnelPayments.addPayment'));
        }
      }
      closeModal();
      fetchPage();
    } catch (error) {
      handleApiError(error, 'Failed to save');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(t('incomes.confirmDelete'))) return;
    try {
      if (row.kind === 'income') {
        await apiEndpoints.miscellaneousPayments.delete(row.id);
      } else if (row.kind === 'expense') {
        await apiEndpoints.institutionalPayments.delete(row.id);
      } else {
        await apiEndpoints.personnelPayments.delete(row.id);
      }
      toast.success(t('incomes.paymentDeleted'));
      fetchPage();
    } catch (error) {
      handleApiError(error, 'Failed to delete');
    }
  };

  const typeBadge = {
    income: { label: t('finance.income'), cls: 'bg-green-100 text-green-700', icon: TrendingUp },
    expense: { label: t('finance.expense'), cls: 'bg-red-100 text-red-700', icon: TrendingDown },
    personnel: { label: t('finance.personnel'), cls: 'bg-blue-100 text-blue-700', icon: Users }
  };

  const tabs = [
    { id: 'all', label: t('finance.all'), icon: List },
    { id: 'income', label: t('finance.income'), icon: TrendingUp },
    { id: 'expense', label: t('finance.expense'), icon: TrendingDown },
    { id: 'personnel', label: t('finance.personnel'), icon: Users }
  ];

  const renderFieldLabel = (icon, color, text) => (
    <label className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-2">
      <span className={`flex items-center justify-center w-6 h-6 rounded-full ${color}`}>
        {icon}
      </span>
      {text}
    </label>
  );

  const inputCls = "w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
  const selectCls = "w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white";

  const fmtAmount = (n) => `${Math.round((n + Number.EPSILON) * 100) / 100} DA`;

  const tabsCount = {
    all: counts.income + counts.expense + counts.personnel,
    income: counts.income,
    expense: counts.expense,
    personnel: counts.personnel
  };

  return (
    <div className="flex flex-col min-h-full bg-neutral-50 px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('finance.title')}</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600">{t('finance.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 flex-nowrap">
          <button
            onClick={() => openModal('income')}
            className="bg-green-600 text-white px-4 py-2.5 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1.5 text-sm font-semibold whitespace-nowrap shadow-sm"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden md:inline">{t('finance.addTransaction')}</span>
          </button>
          <button
            onClick={() => openModal('personnel')}
            className="bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5 text-sm font-semibold whitespace-nowrap shadow-sm"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden md:inline">{t('finance.addPersonnelPayment')}</span>
          </button>
        </div>
      </div>

      {/* Tabs + search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-4">
        <div className="flex flex-wrap items-center gap-2">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setCurrentPage(1); setJumpPage(''); }}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  isActive ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${isActive ? 'bg-white/20' : 'bg-gray-200 text-gray-700'}`}>
                  {tabsCount[tab.id]}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('common.search')}
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={t('finance.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Records list */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-4">
        <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                {activeTab === 'all' && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('finance.type')}</th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('finance.description')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('finance.category')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('finance.date')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('finance.amount')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('finance.status')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('finance.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={activeTab === 'all' ? 7 : 6} className="px-6 py-10 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin text-blue-500 inline-block align-middle" />
                    <span className="ml-2 text-gray-500">{t('common.loading')}</span>
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={activeTab === 'all' ? 7 : 6} className="px-6 py-10 text-center">
                    <Wallet className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">{t('finance.noTransactions')}</p>
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => {
                  const badge = typeBadge[row.kind];
                  const statusInfo = getStatusInfo(row.kind, row.status);
                  const BadgeIcon = badge.icon;
                  return (
                    <tr key={`${row.kind}-${row.id}`} className="hover:bg-gray-50">
                      {activeTab === 'all' && (
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full ${badge.cls}`}>
                            <BadgeIcon className="w-3.5 h-3.5 mr-1" />
                            {badge.label}
                          </span>
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[220px]" title={row.title}>{row.title}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                          {getCategoryLabel(row.kind, row.category)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(row.date).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className={`text-sm font-bold ${row.kind === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                          {row.kind === 'income' ? '+' : '-'}{fmtAmount(row.amount)}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${statusInfo.cls}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-3">
                          <button onClick={() => openModal(row.kind, row)} className="text-blue-600 hover:text-blue-900" title={t('common.edit')}>
                            <Edit className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(row)} className="text-red-600 hover:text-red-900" title={t('common.delete')}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
          <div className="text-xs sm:text-sm text-gray-700 font-medium">
            {t('common.showing')} <span className="font-bold text-blue-600">{totalRecords === 0 ? 0 : (safePage - 1) * itemsPerPage + 1}</span> {t('common.to')} <span className="font-bold text-blue-600">{Math.min(safePage * itemsPerPage, totalRecords)}</span> {t('common.of')} <span className="font-bold text-blue-600">{totalRecords}</span> {t('common.results')}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); setJumpPage(''); }}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg"
            >
              {[10, 25, 50, 100].map(n => (
                <option key={n} value={n}>{n} {t('common.page')}</option>
              ))}
            </select>
            <button
              onClick={() => setCurrentPage(1)}
              disabled={safePage <= 1 || totalPages <= 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.first')}
            </button>
            <button
              onClick={() => setCurrentPage(safePage - 1)}
              disabled={safePage <= 1 || totalPages <= 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.previous')}
            </button>
            <span className="px-3 py-2 text-sm text-gray-700">
              {t('common.page')} {safePage} {t('common.of')} {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(safePage + 1)}
              disabled={safePage >= totalPages || totalPages <= 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.next')}
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={safePage >= totalPages || totalPages <= 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.last')}
            </button>
            <div className="flex items-center gap-1">
              <input
                type="number"
                min="1"
                max={totalPages}
                placeholder={t('common.page')}
                value={jumpPage}
                onChange={(e) => setJumpPage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const p = parseInt(jumpPage, 10);
                    if (p >= 1 && p <= totalPages) setCurrentPage(p);
                    setJumpPage('');
                  }
                }}
                className="w-16 px-2 py-2 border border-gray-300 rounded-lg text-sm text-gray-700"
              />
              <button
                onClick={() => {
                  const p = parseInt(jumpPage, 10);
                  if (p >= 1 && p <= totalPages) setCurrentPage(p);
                  setJumpPage('');
                }}
                className="px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700"
              >
                →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {modalMode && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editing ? t('common.editPayment') : (
                  modalMode === 'income' ? t('finance.newIncome') : modalMode === 'expense' ? t('finance.newExpense') : t('finance.newPersonnelPayment')
                )}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {!editing && (modalMode === 'income' || modalMode === 'expense') && (
                <div className="mb-5">
                  {renderFieldLabel(<List className="w-4 h-4 text-gray-600" />, 'bg-gray-100', `${t('finance.transactionType')} *`)}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => switchTransactionType('income')}
                      className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold border-2 transition-colors ${
                        modalMode === 'income'
                          ? 'bg-green-50 border-green-500 text-green-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-green-300'
                      }`}
                    >
                      <TrendingUp className="w-4 h-4" />
                      {t('finance.income')}
                    </button>
                    <button
                      type="button"
                      onClick={() => switchTransactionType('expense')}
                      className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold border-2 transition-colors ${
                        modalMode === 'expense'
                          ? 'bg-red-50 border-red-500 text-red-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-red-300'
                      }`}
                    >
                      <TrendingDown className="w-4 h-4" />
                      {t('finance.expense')}
                    </button>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {modalMode === 'personnel' && (
                  <div className="md:col-span-2">
                    {renderFieldLabel(<UserCheck className="w-4 h-4 text-blue-600" />, 'bg-blue-100', `${t('common.personnel')} *`)}
                    <select
                      value={form.personnelId}
                      onChange={(e) => setForm({ ...form, personnelId: e.target.value })}
                      required
                      className={selectCls}
                    >
                      <option value="">{t('common.selectPersonnel')}</option>
                      {personnel.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.firstName} {p.lastName} ({p.role === 'Coach' ? t('common.coach') : p.role === 'Admin' ? t('common.admin') : p.role})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {(modalMode === 'expense' || modalMode === 'personnel') && (
                  <div>
                    {renderFieldLabel(<FileText className="w-4 h-4 text-indigo-600" />, 'bg-indigo-100', `${t('common.paymentType')} *`)}
                    {modalMode === 'expense' ? (
                      <select
                        value={form.paymentType}
                        onChange={(e) => setForm({ ...form, paymentType: e.target.value })}
                        required
                        className={selectCls}
                      >
                        {expenseTypes.map(type => <option key={type} value={type}>{type}</option>)}
                      </select>
                    ) : (
                      <select
                        value={form.paymentType}
                        onChange={(e) => setForm({ ...form, paymentType: e.target.value })}
                        required
                        className={selectCls}
                      >
                        {personnelPaymentTypes.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                      </select>
                    )}
                  </div>
                )}

                {(modalMode === 'income' || modalMode === 'expense') && (
                  <div>
                    {renderFieldLabel(<FileText className="w-4 h-4 text-purple-600" />, 'bg-purple-100', `${t('finance.category')} *`)}
                    {modalMode === 'income' ? (
                      <select
                        value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                        required
                        className={selectCls}
                      >
                        <option value="">{t('common.selectCategory')}</option>
                        {incomeCategories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    ) : (
                      <select
                        value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                        required
                        className={selectCls}
                      >
                        {expenseCategories.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    )}
                  </div>
                )}

                {modalMode === 'income' && (
                  <div>
                    {renderFieldLabel(<FileText className="w-4 h-4 text-indigo-600" />, 'bg-indigo-100', `${t('finance.description')} *`)}
                    <input
                      type="text"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      required
                      className={inputCls}
                    />
                  </div>
                )}

                <div>
                  {renderFieldLabel(<DollarSign className="w-4 h-4 text-emerald-600" />, 'bg-emerald-100', `${t('finance.amount')} *`)}
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    required
                    className={inputCls}
                  />
                </div>

                <div>
                  {renderFieldLabel(<Calendar className="w-4 h-4 text-amber-600" />, 'bg-amber-100', `${t('common.paymentDate')} *`)}
                  <input
                    type="date"
                    value={form.paymentDate}
                    onChange={(e) => setForm({ ...form, paymentDate: e.target.value })}
                    required
                    className={inputCls}
                  />
                </div>

                {modalMode === 'expense' && (
                  <div>
                    {renderFieldLabel(<FileText className="w-4 h-4 text-indigo-600" />, 'bg-indigo-100', `${t('finance.description')} *`)}
                    <input
                      type="text"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      required
                      className={inputCls}
                    />
                  </div>
                )}

                {modalMode === 'personnel' && (
                  <div>
                    {renderFieldLabel(<FileText className="w-4 h-4 text-indigo-600" />, 'bg-indigo-100', `${t('finance.description')}`)}
                    <input
                      type="text"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className={inputCls}
                    />
                  </div>
                )}

                {(modalMode === 'expense' || modalMode === 'personnel') && (
                  <div>
                    {renderFieldLabel(<CreditCard className="w-4 h-4 text-purple-600" />, 'bg-purple-100', t('common.paymentMethod'))}
                    <select
                      value={form.paymentMethod}
                      onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}
                      className={selectCls}
                    >
                      {paymentMethods.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                )}

                {(modalMode === 'expense' || modalMode === 'personnel') && (
                  <div>
                    {renderFieldLabel(<CheckCircle className="w-4 h-4 text-green-600" />, 'bg-green-100', t('common.status'))}
                    <select
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: Number(e.target.value) })}
                      className={selectCls}
                    >
                      {(modalMode === 'expense' ? expenseStatusOptions : personnelStatusOptions).map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 shadow-sm"
                >
                  {editing ? t('common.updatePayment') : t('common.createPayment')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinanceOverview;